/*!
 * pixel-bg-webgl.js
 * Replaces the CSS pixel-checkerboard backgrounds with WebGL canvases that
 * distort fluidly on mouse hover.  Respects the site-wide animations-paused
 * state and the per-section parallax checkbox.
 */
(function () {
  'use strict';

  // ── Shaders ────────────────────────────────────────────────────────────────

  var VERT = [
    'attribute vec2 a;',
    'void main() { gl_Position = vec4(a, 0.0, 1.0); }'
  ].join('\n');

  // Procedural checkerboard + sinusoidal flow distortion on hover.
  // uOff allows card canvases to share the same world-space grid as the body
  // canvas (parallax alignment).
  var FRAG = [
    'precision mediump float;',
    'uniform vec2  uRes;',
    'uniform vec2  uMP;',
    'uniform vec2  uOff;',
    'uniform float uTime;',
    'uniform float uStr;',
    'uniform float uPx;',
    'uniform float uRefW;',
    'uniform float uIntensity;',
    'uniform float uDistSize;',
    'uniform vec3  uC1;',
    'uniform vec3  uC2;',
    'void main() {',
    '  vec2 fc = gl_FragCoord.xy + uOff;',
    '  vec2 d  = fc - uMP;',
    '  float dn = length(d) / uRefW;',
    '  float s  = uStr * uIntensity * exp(-dn * dn * uDistSize);',
    '  float wx = sin(fc.y / uPx * 0.6 + uTime * 1.3) * s * uPx * 1.5 + cos(fc.y / uPx * 1.3 + uTime * 0.7) * s * uPx * 0.4;',
    '  float wy = cos(fc.x / uPx * 1.1 + uTime * 0.9) * s * uPx * 0.7 + sin(fc.x / uPx * 0.4 + uTime * 1.7) * s * uPx * 0.3;',
    '  vec2  dir  = normalize(d + vec2(0.0001, 0.0));',
    '  float push = exp(-dn * dn * uDistSize * 5.6) * s * uPx * 2.2;',
    '  vec2 w = fc + vec2(wx, wy) - dir * push;',
    '  float ck = mod(floor(w.x / uPx) + floor(w.y / uPx), 2.0);',
    '  gl_FragColor = vec4(mix(uC1, uC2, ck), 1.0);',
    '}'
  ].join('\n');

  // ── Utilities ──────────────────────────────────────────────────────────────

  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [
      parseInt(hex.substr(0, 2), 16) / 255,
      parseInt(hex.substr(2, 2), 16) / 255,
      parseInt(hex.substr(4, 2), 16) / 255
    ];
  }

  function buildGlContext(canvas) {
    var gl = (
      canvas.getContext('webgl', { alpha: false, antialias: false }) ||
      canvas.getContext('experimental-webgl', { alpha: false, antialias: false })
    );
    if (!gl) return null;

    function compile(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.warn('[pixel-bg-webgl] Shader error:', gl.getShaderInfoLog(s));
        gl.deleteShader(s);
        return null;
      }
      return s;
    }

    var vert = compile(gl.VERTEX_SHADER,   VERT);
    var frag = compile(gl.FRAGMENT_SHADER, FRAG);
    if (!vert || !frag) return null;

    var prog = gl.createProgram();
    gl.attachShader(prog, vert);
    gl.attachShader(prog, frag);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn('[pixel-bg-webgl] Link error:', gl.getProgramInfoLog(prog));
      return null;
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1,  1, -1,  -1, 1,  1, 1]),
      gl.STATIC_DRAW
    );
    var aLoc = gl.getAttribLocation(prog, 'a');
    gl.enableVertexAttribArray(aLoc);
    gl.vertexAttribPointer(aLoc, 2, gl.FLOAT, false, 0, 0);

    return {
      gl: gl,
      u: {
        res:       gl.getUniformLocation(prog, 'uRes'),
        mp:        gl.getUniformLocation(prog, 'uMP'),
        off:       gl.getUniformLocation(prog, 'uOff'),
        time:      gl.getUniformLocation(prog, 'uTime'),
        str:       gl.getUniformLocation(prog, 'uStr'),
        px:        gl.getUniformLocation(prog, 'uPx'),
        refw:      gl.getUniformLocation(prog, 'uRefW'),
        intensity: gl.getUniformLocation(prog, 'uIntensity'),
        distSize:  gl.getUniformLocation(prog, 'uDistSize'),
        c1:        gl.getUniformLocation(prog, 'uC1'),
        c2:        gl.getUniformLocation(prog, 'uC2')
      }
    };
  }

  // ── PixelLayer ─────────────────────────────────────────────────────────────

  function PixelLayer(opts) {
    var self      = this;
    self.dpr      = window.devicePixelRatio || 1;
    self.pxSize   = (opts.pxSize || 4) * self.dpr;
    self.hover    = 0;
    self.target   = 0;
    self.time     = 0;
    self.raf      = null;
    self.lastTs   = 0;
    self.paused   = false;
    self.visible  = true;
    // Mouse + world offset in device pixels (WebGL y = 0 at bottom)
    self.mpX = 0; self.mpY = 0;
    self.offX = 0; self.offY = 0;
    self.refW = 1;

    self.canvas = document.createElement('canvas');
    self.canvas.style.cssText = opts.style || '';

    var container = opts.container || document.body;
    container.insertBefore(self.canvas, container.firstChild);

    var ctx = buildGlContext(self.canvas);
    if (!ctx) { self.ok = false; return; }
    self.ok = true;
    self.gl = ctx.gl;
    self.u  = ctx.u;

    var cfg = window.pixelBgConfig || {};
    self.gl.uniform1f(self.u.px,        self.pxSize);
    self.gl.uniform1f(self.u.intensity,  opts.distortionIntensity != null ? opts.distortionIntensity : (cfg.distortionIntensity != null ? cfg.distortionIntensity : 1.0));
    self.gl.uniform1f(self.u.distSize,   opts.distortionSize      != null ? opts.distortionSize      : (cfg.distortionSize      != null ? cfg.distortionSize      : 5.0));
    self.gl.uniform3fv(self.u.c1, opts.c1 || hexToRgb('#f0f0f0'));
    self.gl.uniform3fv(self.u.c2, opts.c2 || hexToRgb('#e0e0e0'));
    self.gl.uniform2f(self.u.off, 0, 0);

    self.autoAnimate         = opts.autoAnimate || false;
    self.autoAnimateStrength  = opts.autoAnimateStrength != null ? opts.autoAnimateStrength : 0.5;

    self._tick = self._tick.bind(self);
  }

  PixelLayer.prototype.resize = function (w, h) {
    var d = this.dpr;
    this.canvas.width  = Math.round(w * d);
    this.canvas.height = Math.round(h * d);
    this.canvas.style.width  = w + 'px';
    this.canvas.style.height = h + 'px';
    if (this.ok) {
      this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      this.gl.uniform2f(this.u.res, this.canvas.width, this.canvas.height);
    }
  };

  PixelLayer.prototype.render = function () {
    if (!this.ok) return;
    var gl = this.gl, u = this.u;
    gl.uniform2f(u.mp,   this.mpX,  this.mpY);
    gl.uniform2f(u.off,  this.offX, this.offY);
    gl.uniform1f(u.time, this.time);
    gl.uniform1f(u.str,  this.hover);
    gl.uniform1f(u.refw, this.refW);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  PixelLayer.prototype._tick = function (ts) {
    var dt    = Math.min((ts - this.lastTs) / 1000, 0.05);
    this.lastTs = ts;
    this.time  += dt;

    // pausePending: ease out to static, then stop
    if (this.pausePending) {
      this.target = 0;
      this.hover += (0 - this.hover) * Math.min(dt * 5, 1);
      this.render();
      if (this.hover < 0.005) {
        this.hover = 0;
        this.render();
        this.raf = null;
        this.pausePending = false;
        // If the user unpaused while the ease-out was still running,
        // kick() was a no-op (raf was busy). Restart now that raf is free.
        if (!this.paused) this.kick();
      } else {
        this.raf = requestAnimationFrame(this._tick);
      }
      return;
    }

    if (this.autoAnimate) {
      // Orbit a virtual mouse slowly around the card centre
      var cx = this.canvas.width  * 0.5;
      var cy = this.canvas.height * 0.5;
      var r  = Math.min(this.canvas.width, this.canvas.height) * 0.25;
      this.mpX   = cx + r * Math.cos(this.time * 0.4);
      this.mpY   = cy + r * Math.sin(this.time * 0.29);
      this.target = this.autoAnimateStrength;
    }

    this.hover += (this.target - this.hover) * Math.min(dt * 5, 1);
    this.render();

    if (this.autoAnimate || this.hover > 0.005 || this.target > 0) {
      this.raf = requestAnimationFrame(this._tick);
    } else {
      this.hover = 0;
      this.render(); // settle to static frame
      this.raf = null;
    }
  };

  PixelLayer.prototype.kick = function () {
    // Allow kick during pause ease-out (pausePending), block otherwise when paused
    if (!this.raf && (!this.paused || this.pausePending) && this.visible) {
      this.lastTs = performance.now();
      this.raf = requestAnimationFrame(this._tick);
    }
  };

  PixelLayer.prototype.stop = function () {
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
  };

  // ── Pause integration ──────────────────────────────────────────────────────

  function watchPause(layer) {
    function sync() {
      var wasPaused = layer.paused;
      layer.paused = document.documentElement.classList.contains('animations-paused');
      if (layer.paused) {
        // Begin ease-out: keep the RAF alive so _tick can smoothly settle to zero
        layer.pausePending = true;
        layer.target = 0;
        layer.kick(); // ensure RAF is running so ease-out plays
      } else if (wasPaused) {
        // Resuming — restart any card that is already visible
        layer.kick();
      }
    }
    new MutationObserver(sync).observe(
      document.documentElement,
      { attributes: true, attributeFilter: ['class'] }
    );
    sync();
  }

  // ── Body background ────────────────────────────────────────────────────────

  function setupBody() {
    var layer = new PixelLayer({
      pxSize: 12,
      c1: hexToRgb('#ffffff'),
      c2: hexToRgb('#f2f2f2'),
      style: 'position:absolute;top:0;left:0;z-index:-1;pointer-events:none;display:block;'
    });
    if (!layer.ok) return;

    document.body.style.backgroundImage = 'none';
    document.body.style.position = 'relative';

    function syncSize() {
      // Zero out canvas height first so it doesn't inflate scrollHeight
      layer.canvas.style.height = '0px';
      layer.canvas.height = 0;
      var h = Math.max(
        document.body.scrollHeight, document.documentElement.scrollHeight,
        document.body.offsetHeight, document.documentElement.offsetHeight
      );
      layer.refW = Math.round(window.innerWidth / 3) * layer.dpr;
      layer.resize(window.innerWidth, h);
      layer.render();
    }

    syncSize();

    window.addEventListener('resize', syncSize);

    // Re-sync when content changes height (lazy-loaded sections etc.)
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(syncSize).observe(document.documentElement);
    }

    document.addEventListener('mousemove', function (e) {
      var h = layer.canvas.height / layer.dpr;
      layer.mpX = e.clientX * layer.dpr;
      layer.mpY = (h - (e.clientY + window.scrollY)) * layer.dpr;
      layer.target = 1;
      layer.kick();
    });

    document.documentElement.addEventListener('mouseleave', function () {
      layer.target = 0; layer.kick();
    });

    watchPause(layer);
  }

  // ── Card backgrounds ───────────────────────────────────────────────────────

  function setupCards(autoAnimate) {
    document.querySelectorAll('.category-card').forEach(function (card) {
      setupCard(card, autoAnimate);
    });
  }

  function setupCard(card, autoAnimate) {
    var layer = new PixelLayer({
      pxSize: 12,
      autoAnimate: autoAnimate || false,
      c1: hexToRgb('#ffffff'),
      c2: hexToRgb('#cfdde0'),
      container: card,
      style: 'position:absolute;top:0;left:0;z-index:-1;pointer-events:none;display:block;'
    });
    if (!layer.ok) return;

    card.style.backgroundImage = 'none';

    function syncSize() {
      layer.resize(card.offsetWidth, card.offsetHeight);
      layer.offX = 0;
      layer.offY = 0;
      layer.refW = card.offsetWidth * layer.dpr;
      layer.render();
    }

    syncSize();

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(syncSize).observe(card);
    }

    if (!autoAnimate) {
      card.addEventListener('mouseenter', function () {
        layer.target = 1; layer.kick();
      });
      card.addEventListener('mouseleave', function () {
        layer.target = 0; layer.kick();
      });
      card.addEventListener('mousemove', function (e) {
        var d = layer.dpr;
        var r = card.getBoundingClientRect();
        layer.mpX = (e.clientX - r.left) * d;
        layer.mpY = (r.bottom - e.clientY) * d;
        layer.kick();
      });
    }

    // Stop rendering when the card scrolls off-screen; restart auto-animation when it returns
    if (typeof IntersectionObserver !== 'undefined') {
      new IntersectionObserver(function (entries) {
        layer.visible = entries[0].isIntersecting;
        if (!layer.visible) {
          layer.stop();
        } else if (autoAnimate) {
          layer.kick();
        }
      }).observe(card);
    }

    // Kick auto-animation immediately
    if (autoAnimate) layer.kick();

    watchPause(layer);
  }

  // ── Boot ───────────────────────────────────────────────────────────────────

  function init() {
    var isTouch  = window.matchMedia('(pointer: coarse)').matches;
    var isMobile = window.matchMedia('(max-width: 1024px)').matches;

    try {
      var t = document.createElement('canvas');
      if (!t.getContext('webgl') && !t.getContext('experimental-webgl')) return;
    } catch (e) { return; }

    if (isTouch || isMobile) {
      // Mobile / touch: cards only with automatic looping distortion
      setupCards(true);
    } else {
      setupBody();
      setupCards(false);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
